'use client';

import { Dialog, DialogBackdrop, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import React, { Fragment, useState } from 'react';

import { ActionIcon } from '@/components/ActionIcon';
import Button from '@/components/button/Button';
import VideoAwareAutoHyperlink from '@/components/video/VideoAwareAutoHyperlink';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { cookieStorage } from '@cineamo/ui/src/helpers/cookie-helper';

import { IconClose24 } from '@cineamo/ui/src/icons/ic-close';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const COOKIE_INFO_BANNER_POPUP_CLOSED = 'info-banner-popup-closed';

type InfoBannerPopupProps = {
    text: string;
};

const InfoBannerPopup: React.FC<InfoBannerPopupProps> = ({ text }) => {
    const domainConfiguration = useDomainConfiguration();
    const [isOpen, setIsOpen] = useState(() => !cookieStorage.getItem(COOKIE_INFO_BANNER_POPUP_CLOSED));

    function handleClose() {
        cookieStorage.setItem(COOKIE_INFO_BANNER_POPUP_CLOSED, true, {
            path: '/'
        });
        setIsOpen(false);
    }

    if (!isOpen) {
        return null;
    }

    return (
        <Transition
            show={isOpen}
            as={Fragment}>
            <Dialog
                as="div"
                className="relative z-[60]"
                open={isOpen}
                onClose={handleClose}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <DialogBackdrop
                        className="fixed inset-0 backdrop-blur-sm"
                        style={{
                            backgroundColor: withOpacity(domainConfiguration.style?.backgroundColor, 0.15)
                        }}
                    />
                </TransitionChild>

                <div className="fixed inset-0 flex items-center justify-center p-24">
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95">
                        <DialogPanel
                            className="relative max-w-[600px] w-full rounded-8 p-24 md:p-32 shadow-lg"
                            style={{
                                backgroundColor: withOpacity(domainConfiguration.style?.backgroundColor, 1.0),
                                color: domainConfiguration.style?.onSurfaceColor
                            }}>
                            <div
                                className="absolute right-8 top-8"
                                style={{ color: domainConfiguration.style?.onSurfaceColor }}>
                                <ActionIcon
                                    icon={
                                        <IconClose24
                                            fill={domainConfiguration.style?.onSurfaceColor}
                                            className="opacity-60"
                                        />
                                    }
                                    action={handleClose}
                                />
                            </div>
                            <div
                                className="whitespace-pre-wrap text-body-16 text-center pt-16"
                                style={{ color: domainConfiguration.style?.onSurfaceColor }}>
                                <VideoAwareAutoHyperlink trackingEvent="Info Banner Video Played">
                                    {text}
                                </VideoAwareAutoHyperlink>
                            </div>
                            <div className="flex justify-center mt-24">
                                <Button
                                    css={{
                                        backgroundColor: domainConfiguration.style?.primaryColor,
                                        color: domainConfiguration.style?.onPrimaryColor
                                    }}
                                    onClick={handleClose}>
                                    OK
                                </Button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    );
};

export default InfoBannerPopup;
